import { Component, OnInit } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { LocationService } from '@app/core/location';
import { ParametersService, ParamType } from '@app/core/parameters';
import {
  IGetPatientListByRequest,
  IGetPatientListRequest,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { addDaysToDate } from '@app/core/date-utils';
import { MsgboxService } from '@app/shared/msgbox';
import { IPatientSearchInfo } from '../search-by-patient/search-by-patient.component';
import { IRequestSearchInfo } from '../search-by-request/search-by-request.component';

@Component({
  selector: 'app-patient-requests',
  templateUrl: './patient-requests.component.html',
})
export class PatientRequestsComponent implements OnInit {
  //we save previous search values
  public patientSearch$: Observable<IPatientSearchInfo>;
  public requestSearch$: Observable<IRequestSearchInfo>;
  public activeTabIndex: number = 0;

  constructor(
    private currentRoute: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private service: PatientRequestsService,
    private paramsService: ParametersService,
    private msgbox: MsgboxService
  ) {
    // WIR-RF-01.01.15: valor por defecto "Fecha extracción desde" = parámetro 5 (días por defecto); param 6 = máximo permitido
    this.patientSearch$ = this.paramsService
      .getParameters([
        ParamType.SearchRequestsNumberOfDays,
        ParamType.SearchRequestsMaxDays,
      ])
      .pipe(
        map((p) => {
          const defaultSearchDaysParam = p.find(
            (pp) => pp.paramType === ParamType.SearchRequestsNumberOfDays
          );
          const maxSearchDaysParam = p.find(
            (pp) => pp.paramType === ParamType.SearchRequestsMaxDays
          );
          const defaultSearchDays = defaultSearchDaysParam
            ? Number.parseInt(defaultSearchDaysParam.value, 10)
            : 30;
          const maxSearchDays = maxSearchDaysParam
            ? Number.parseInt(maxSearchDaysParam.value, 10)
            : 30;

          const previousSearch = this.service.getPreviousPatientSearch();

          const defaultSearch: IGetPatientListRequest = {
            extractionFrom: addDaysToDate(new Date(), -defaultSearchDays),
            extractionTo: new Date(),
          };

          if (previousSearch) {
            //give default for stored searches
            if (
              !previousSearch.extractionFrom &&
              !previousSearch.extractionTo &&
              !previousSearch.birthDate
            ) {
              Object.assign(previousSearch, defaultSearch);
            } else {
              previousSearch.extractionFrom = previousSearch.extractionFrom
                ? new Date(previousSearch.extractionFrom)
                : null;
              previousSearch.extractionTo = previousSearch.extractionTo
                ? new Date(previousSearch.extractionTo)
                : null;
              previousSearch.birthDate = previousSearch.birthDate
                ? new Date(previousSearch.birthDate)
                : null;
            }
          }

          return {
            previousSearch,
            defaultSearch,
            maxSearchDays,
          };
        })
      );

    this.requestSearch$ = this.paramsService
      .getParameters([ParamType.SearchRequestsMaxDays])
      .pipe(
        map((p) => {
          const maxSearchDays = parseInt(p[0].value);
          const previousSearch = this.service.getPreviousRequestSearch();

          // WIR-RF-02.01.13: fechas por defecto solo si hay rango petición/centro (se aplican en el hijo)
          const defaultSearch: IGetPatientListByRequest = {};

          if (previousSearch) {
            previousSearch.extractionFrom = previousSearch.extractionFrom
              ? this.parseEuropeanDate(previousSearch.extractionFrom)
              : null;
            previousSearch.extractionTo = previousSearch.extractionTo
              ? this.parseEuropeanDate(previousSearch.extractionTo)
              : null;
          }

          return {
            previousSearch,
            defaultSearch,
            maxSearchDays,
          };
        })
      );
  }
  ngOnInit(): void {
    const searchType = this.currentRoute.snapshot.queryParams['searchType'];
    if (searchType === 'search-by-patient') {
      this.activeTabIndex = 0;
    } else if (searchType === 'search-by-request') {
      this.activeTabIndex = 1;
    }
  }

  private parseEuropeanDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day)); // Month is 0-indexed
  }

  public onSearchByPatient($event): void {
    this.service.setPatientSearch($event);

    const encodedParams = this.locationService.encodeParams($event);

    const decodedParams = this.locationService.decodeParams(encodedParams);

    this.service.getPatientHistories(decodedParams).subscribe({
      next: (data) => {
        this.router.navigate(['./histories'], {
          queryParams: encodedParams,
          relativeTo: this.currentRoute,
          skipLocationChange: false,
          state: { data }, // Pass the data as state
        });
      },
      error: (err) => {
        const key = err?.error?.message || err?.message;
        if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
          this.msgbox.showError(key);
        } else {
          this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
        }
      },
    });
  }

  public onSearchByRequest($event): void {
    this.service.setRequestSearch($event);

    const encodedParams = this.locationService.encodeParams($event);
    const decodedParams = this.locationService.decodeParams(encodedParams);

    this.service.searchRequests(decodedParams).subscribe((data) => {
      this.router.navigate(['./requests'], {
        queryParams: $event,
        relativeTo: this.currentRoute,
        skipLocationChange: false,
        state: { data }, // Pass the data as state
      });
    });
  }
}
